import React from "react";

// App-wide error boundary. React error boundaries MUST be class components — there's no
// hook equivalent. Wraps <App/> so any render/lifecycle throw shows a friendly recover
// screen instead of a blank white page (which is what users got during the TDZ crash —
// no way for them to know what happened or what to do).
//
// Deliberately self-contained: no theme tokens, no app imports. If App is broken, this
// must still render with zero dependencies on the thing that just crashed.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the full trace to the console so it's debuggable from DevTools / remote logs.
    console.error("[ErrorBoundary] caught a render error:", error, info?.componentStack);
    // Best-effort: stash the last error in localStorage so a user reporting "it broke"
    // can be asked to read it back, even after they reload.
    try {
      localStorage.setItem(
        "exator:lastError",
        JSON.stringify({
          message: String(error?.message || error),
          stack: String(error?.stack || "").slice(0, 2000),
          when: new Date().toISOString(),
        })
      );
    } catch (_) {
      /* localStorage can be unavailable in private mode — ignore */
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = String(this.state.error?.message || this.state.error || "Unknown error");
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          fontFamily: "-apple-system, BlinkMacSystemFont, system-ui, sans-serif",
          background: "#FFF8F2",
          color: "#2D2A26",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 44, marginBottom: 12 }}>🌧️</div>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
          Something broke
        </h1>
        <p
          style={{
            fontSize: 14,
            color: "#7A756E",
            lineHeight: 1.6,
            maxWidth: 320,
            marginBottom: 20,
          }}
        >
          Exator hit an unexpected error and stopped rendering. Your data is safe —
          it's all stored in the cloud. Reloading usually fixes it.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "12px 28px",
            borderRadius: 12,
            background: "linear-gradient(135deg,#E8724A,#D4612F)",
            color: "white",
            border: "none",
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
            boxShadow: "0 4px 12px rgba(232,114,74,0.3)",
            marginBottom: 14,
          }}
        >
          Reload Exator
        </button>
        <details
          style={{
            fontSize: 11,
            color: "#B5AFA7",
            maxWidth: 320,
            textAlign: "left",
          }}
        >
          <summary style={{ cursor: "pointer", marginBottom: 6 }}>
            Technical details
          </summary>
          <code
            style={{
              display: "block",
              wordBreak: "break-word",
              fontSize: 10,
              lineHeight: 1.5,
              background: "#FFF0E6",
              padding: "8px 10px",
              borderRadius: 8,
            }}
          >
            {msg}
          </code>
          <p style={{ marginTop: 8, lineHeight: 1.5 }}>
            If this keeps happening, email a screenshot of this to{" "}
            <a href="mailto:jbencosme94@gmail.com" style={{ color: "#E8724A" }}>
              jbencosme94@gmail.com
            </a>
            .
          </p>
        </details>
      </div>
    );
  }
}
